/**
 * Ask layer core: the answer path, as pure functions over the loaded catalog.
 *
 * Run:       not run — `handlers.ts` (and through it every adapter) calls `search()` and
 *            `recordFeedback()`; `answer()` is the same computation without the telemetry row,
 *            for previews and tests.
 * EDIT:      NOT_FOUND_MESSAGE — one fixed sentence per language, quoting two jobs from YOUR
 *            catalog. Nothing else: `limit + 1`, the card shape and what gets logged are what the
 *            panel and the reports expect.
 */
import {
  CATALOG_BASE_LANGUAGE,
  CATALOG_DEFAULT_ANCHOR_ACTION,
  type AssistantAnswer,
  type AssistantResultCard,
  type AssistantSearchResponse,
  type CatalogLanguage,
  type TenantId,
} from './types';
import { catalogHash, catalogIndex, viewEntry } from './catalog';
import { emitProductEvent, interactionKey, recordInteraction } from './interaction-log';
import { isConfident, retrieve, type RankedEntry, type RetrieveContext } from './search';

/**
 * The assistant's answer path.
 *
 * Phase 1 answers with catalog cards only — the entry's own reviewed words, verbatim. There is
 * no generation step, so there is nothing to hallucinate; an entry that reads badly is a content
 * bug fixed in `catalog/`, not a prompt to tune.
 *
 * The `AssistantAnswer` envelope carries `kind` for exactly one reason: Plan A (an LLM composing
 * a reply from these same retrieved entries) becomes `kind: 'generated'` with `text` filled in
 * and the cards kept as citations. The panel already renders both shapes, so that upgrade is a
 * change here, not a redesign.
 */

/**
 * Fixed wording. The one thing never done here is inventing an answer to keep the rate up.
 * EDIT: one sentence per CATALOG_LANGUAGES entry; quote two real jobs from your catalog.
 */
export const NOT_FOUND_MESSAGE: Record<CatalogLanguage, string> = {
  en: "I don't have an answer for that yet. Try naming the screen or the action — for example \"import a bank statement\" or \"send a reminder\".",
  lt: 'Į tai dar neturiu atsakymo. Pabandykite įvardyti ekraną arba veiksmą — pavyzdžiui, „importuoti banko išrašą“ arba „išsiųsti priminimą“.',
  lv: 'Uz to man vēl nav atbildes. Mēģiniet nosaukt ekrānu vai darbību — piemēram, “importēt bankas izrakstu” vai “nosūtīt atgādinājumu”.',
};

/**
 * `crypto.randomUUID()` is global in Node 20+ and in every browser on a secure context — which
 * is where an authenticated app runs. A page served over plain http:// (a dev proxy, say) has
 * `crypto` but no `randomUUID`; the id only correlates a search with its feedback, so a
 * non-cryptographic fallback loses nothing there.
 */
const newQueryId = (): string =>
  typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `q-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const toCard = (hit: RankedEntry, language: CatalogLanguage): AssistantResultCard => {
  const view = viewEntry(hit.entry, language);
  return {
    id: hit.entry.id,
    area: hit.entry.area,
    job: view.job,
    description: view.description,
    prerequisites: view.prerequisites,
    roles: hit.entry.roles,
    deeplink: hit.entry.deeplink,
    anchor: hit.entry.anchor ?? null,
    // Resolved here rather than on the client, so "is this control on the page we are about to
    // send you to?" is one comparison in the panel instead of a null-aware rule it could get
    // wrong — which is exactly how off-page anchors went unnoticed for a whole catalog.
    anchorRoute: hit.entry.anchor_route ?? hit.entry.deeplink,
    // Null rather than absent, for the same reason `anchorRoute` is always filled: the panel
    // asks "is there a surface to open?" once, and a field that is sometimes missing is a
    // question it can get wrong.
    anchorSurface: hit.entry.anchor_surface ?? null,
    anchorAction: hit.entry.anchor_action ?? CATALOG_DEFAULT_ANCHOR_ACTION,
    score: Math.round(hit.score * 1000) / 1000,
    language: view.language,
    fallback: view.fallback,
  };
};

/** What the panel sends, as the validator hands it over. */
export interface AnswerContext {
  screen?: string | null;
  language?: CatalogLanguage;
  limit?: number;
  roles?: readonly string[];
}

export interface SearchInput extends AnswerContext {
  question: string;
  /** For the miss log — never used to filter results. */
  userId?: string;
  /** Your tenant / organisation scope, if the app has one. For the miss log only. */
  tenantId?: TenantId;
}

interface Resolved {
  response: AssistantSearchResponse;
  hits: RankedEntry[];
  confident: boolean;
}

/**
 * Retrieval seam. Everything above it depends on "question in, ranked entries out" and on
 * nothing else about how the ranking is done.
 */
export const retrieveFromCatalog = (question: string, context: RetrieveContext = {}): RankedEntry[] =>
  retrieve(catalogIndex(), question, context);

const resolve = (question: string, ctx: AnswerContext): Resolved => {
  const language = ctx.language ?? CATALOG_BASE_LANGUAGE;
  const limit = ctx.limit ?? 4;

  // One extra hit is retrieved so a near miss can still be offered as a suggestion.
  const hits = retrieveFromCatalog(question, {
    screen: ctx.screen ?? null,
    language,
    roles: ctx.roles,
    limit: limit + 1,
  });

  const queryId = newQueryId();
  const confident = isConfident(hits[0]);
  const entries = confident ? hits.slice(0, limit).map((hit) => toCard(hit, language)) : [];
  const answer: AssistantAnswer = { kind: 'cards', entries };

  if (confident) {
    return { response: { queryId, matchFound: true, answer, language }, hits, confident };
  }
  return {
    response: {
      queryId,
      matchFound: false,
      answer,
      language,
      notFound: {
        message: NOT_FOUND_MESSAGE[language] ?? NOT_FOUND_MESSAGE[CATALOG_BASE_LANGUAGE],
        // Nearest jobs, offered as "did you mean", never as an answer to what was asked.
        suggestions: hits.slice(0, 3).map((hit) => viewEntry(hit.entry, language).job),
      },
    },
    hits,
    confident,
  };
};

/** The answer alone — cards or an honest not-found — with nothing logged. */
export const answer = (question: string, ctx: AnswerContext = {}): AssistantSearchResponse =>
  resolve(question, ctx).response;

/** The answer, AND the interaction recorded (after the settle window). What `POST /search` does. */
export const search = (input: SearchInput): AssistantSearchResponse => {
  const { response, hits, confident } = resolve(input.question, input);
  const language = response.language;

  // The miss log is the steering instrument for the catalog: a question nobody could answer is
  // either a vocabulary gap (add aliases) or a content gap (write an entry). Both need the
  // question itself, so it is logged verbatim, without any of the user's data around it.
  //
  // `catalogHash` is what makes the miss log a TREND rather than a list. Without it, "we added
  // twelve entries last week — did the misses drop?" cannot be answered: every number would be
  // an average over whatever catalogs happened to be deployed in the window.
  //
  // Held for a settle window instead of emitted here, because the panel searches as you type and
  // a half-typed word is a miss against nothing: `new o` and `new ordr` were logged as two
  // unanswered questions a second apart. See `interaction-log.ts` — the request still happens,
  // only the record waits for the question to stand still.
  recordInteraction({
    key: interactionKey(input.userId, input.tenantId),
    question: input.question,
    event: {
      name: 'AssistantInteraction',
      properties: {
        queryId: response.queryId,
        question: input.question,
        screen: input.screen ?? null,
        language,
        matchFound: confident,
        topId: hits[0]?.entry.id ?? null,
        catalogHash: catalogHash(),
        tenantId: input.tenantId ?? null,
        userId: input.userId ?? null,
      },
      // Measurements, not dimensions: these are averaged and percentiled in the reports, and a
      // score kept as a string needs a cast in every query that touches it.
      measurements: {
        topScore: hits[0] ? Math.round(hits[0].score * 1000) / 1000 : null,
        coverage: hits[0] ? Math.round(hits[0].coverage * 100) / 100 : null,
        resultCount: response.answer.entries.length,
      },
      summary: confident ? 'assistant answered' : 'assistant miss',
    },
  });

  return response;
};

/**
 * Feedback is telemetry, not a record: it steers which entries get rewritten, and keeping a
 * user's verbatim opinion in the operational database would be a data-retention question
 * nobody asked for. It goes to the telemetry sink — see `interaction-log.ts` for why a product
 * event is not a log line.
 *
 * `helpful` and `deeplinkFollowed` are logged as separate, independently-nullable fields: a
 * deep-link beacon carries no verdict, and recording one anyway (as this once did) makes the
 * thumbs-up rate meaningless. `null` means "not stated", never "false".
 */
export const recordFeedback = (input: {
  queryId: string;
  helpful?: boolean;
  comment?: string;
  deeplinkFollowed?: boolean;
  userId?: string;
  tenantId?: TenantId;
}): void => {
  emitProductEvent({
    name: 'AssistantFeedback',
    properties: {
      queryId: input.queryId,
      // Omitted rather than nulled when unstated (the sink drops nulls), so a query for
      // "helpful is set" means "the user gave a verdict" and a deep-link beacon does not count.
      helpful: input.helpful ?? null,
      deeplinkFollowed: input.deeplinkFollowed ?? null,
      comment: input.comment ?? null,
      catalogHash: catalogHash(),
      tenantId: input.tenantId ?? null,
      userId: input.userId ?? null,
    },
    summary: 'assistant feedback',
  });
};

/** The same four functions as one object, for code that prefers `assistantService.search(...)`. */
export const assistantService = { retrieve: retrieveFromCatalog, answer, search, recordFeedback };
